import React, { useState, useEffect, useMemo } from 'react';
import { paymentsAPI } from '../../services/api';
import { formatAmount } from '../../utils/formatters';
import { TrendingUp } from 'lucide-react';
import { Spinner } from './LoadingComponents';
import './SpendingTrendChart.css';

const PERIOD_OPTIONS = [
  { value: '1w', label: '1 week' },
  { value: 'mtd', label: 'MTD' },
  { value: '1m', label: '1 month' },
  { value: '3m', label: '3 months' },
  { value: '6m', label: '6 months' },
  { value: 'ytd', label: 'YTD' },
  { value: '1y', label: '1 year' }
];

function SpendingTrendChart() {
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('1m');
  const [hoveredPoint, setHoveredPoint] = useState(null);

  useEffect(() => {
    loadTrendData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const loadTrendData = async () => {
    setLoading(true);
    try {
      const response = await paymentsAPI.getTrend(period);
      setTrendData(response.data);
    } catch (error) {
      console.error('Failed to load trend data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format Y-axis values (compact format for large numbers)
  const formatYAxisValue = (value) => {
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}K`;
    }
    return `$${Math.round(value)}`;
  };

  const chartData = useMemo(() => {
    if (!trendData?.dataPoints?.length) return null;

    const points = trendData.dataPoints;
    const values = points.map(p => p.cumulativeAmount);
    const maxValue = Math.max(...values, 1);
    const minValue = Math.min(...values, 0);
    const range = maxValue - minValue || 1;

    // SVG dimensions with padding
    const width = 100;
    const height = 100;
    const padding = { top: 2, right: 2, bottom: 2, left: 2 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Generate path coordinates
    const pathPoints = points.map((point, index) => {
      const x = padding.left + (index / Math.max(points.length - 1, 1)) * chartWidth;
      const y = padding.top + chartHeight - ((point.cumulativeAmount - minValue) / range) * chartHeight;
      return { x, y, data: point };
    });

    // Create SVG path
    const linePath = pathPoints.map((p, i) => 
      `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`
    ).join(' ');

    // Create area path (for gradient fill)
    const areaPath = `${linePath} L ${pathPoints[pathPoints.length - 1].x.toFixed(2)} ${height - padding.bottom} L ${padding.left} ${height - padding.bottom} Z`;

    // Y-axis values (top, middle, bottom)
    const yAxisValues = [maxValue, (maxValue + minValue) / 2, minValue];

    // X-axis labels - just show first, middle, and last dates
    const xLabels = [
      points[0]?.date,
      points[Math.floor(points.length / 2)]?.date,
      points[points.length - 1]?.date
    ].filter(Boolean);

    return {
      pathPoints,
      linePath,
      areaPath,
      yAxisValues,
      xLabels,
      width,
      height,
      maxValue,
      minValue
    };
  }, [trendData]);

  const formatDateLabel = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTooltipDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const handleMouseMove = (e) => {
    if (!chartData) return;
    
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * chartData.width;
    
    // Find closest point
    let closestPoint = null;
    let closestDist = Infinity;
    
    chartData.pathPoints.forEach(point => {
      const dist = Math.abs(point.x - x);
      if (dist < closestDist) {
        closestDist = dist;
        closestPoint = point;
      }
    });
    
    if (closestPoint) {
      setHoveredPoint(closestPoint);
    }
  };

  const handleMouseLeave = () => {
    setHoveredPoint(null);
  };

  if (loading) {
    return (
      <div className="spending-trend-card">
        <div className="spending-trend-header">
          <div className="spending-trend-title">
            <span className="title-label">TOTAL PAID</span>
          </div>
        </div>
        <div className="spending-trend-loading">
          <Spinner size="md" />
        </div>
      </div>
    );
  }

  const summary = trendData?.summary;

  return (
    <div className="spending-trend-card">
      <div className="spending-trend-header">
        <div className="spending-trend-title">
          <span className="title-label">TOTAL PAID</span>
          <div className="spending-value-row">
            <span className="spending-value">{formatAmount(summary?.total || 0)}</span>
          </div>
        </div>
        <div className="spending-trend-controls">
          <select 
            className="period-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            {PERIOD_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {chartData && chartData.pathPoints.length > 1 ? (
        <div className="spending-trend-chart">
          <div className="chart-wrapper">
            {/* Y-axis labels */}
            <div className="y-axis-labels">
              {chartData.yAxisValues.map((value, i) => (
                <span key={i} className="y-label">
                  {formatYAxisValue(value)}
                </span>
              ))}
            </div>

            {/* Chart area */}
            <div className="chart-area">
              <svg 
                viewBox={`0 0 ${chartData.width} ${chartData.height}`}
                preserveAspectRatio="none"
                className="trend-svg"
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
              >
                <defs>
                  <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--chart-color)" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="var(--chart-color)" stopOpacity="0.05" />
                  </linearGradient>
                </defs>
                
                {/* Grid lines */}
                <line x1="0" y1="2" x2="100" y2="2" className="grid-line" />
                <line x1="0" y1="50" x2="100" y2="50" className="grid-line" />
                <line x1="0" y1="98" x2="100" y2="98" className="grid-line" />
                
                {/* Area fill */}
                <path d={chartData.areaPath} className="trend-area" fill="url(#areaGradient)" />
                
                {/* Line */}
                <path d={chartData.linePath} className="trend-line" />
                
                {/* Hover indicator */}
                {hoveredPoint && (
                  <>
                    <line 
                      x1={hoveredPoint.x} 
                      y1={2} 
                      x2={hoveredPoint.x} 
                      y2={98}
                      className="hover-line"
                    />
                    <circle 
                      cx={hoveredPoint.x} 
                      cy={hoveredPoint.y} 
                      r="3"
                      className="hover-dot"
                    />
                  </>
                )}
              </svg>

              {/* Tooltip */}
              {hoveredPoint && (
                <div 
                  className="chart-tooltip"
                  style={{ left: `${(hoveredPoint.x / chartData.width) * 100}%` }}
                >
                  <div className="tooltip-date">{formatTooltipDate(hoveredPoint.data.date)}</div>
                  <div className="tooltip-value">{formatAmount(hoveredPoint.data.cumulativeAmount)}</div>
                  {hoveredPoint.data.dailyAmount > 0 && (
                    <div className="tooltip-daily">+{formatAmount(hoveredPoint.data.dailyAmount)} today</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* X-axis labels */}
          <div className="x-axis-labels">
            {chartData.xLabels.map((date, i) => (
              <span key={i} className="x-label">
                {formatDateLabel(date)}
              </span>
            ))}
          </div>
        </div>
      ) : (
        <div className="spending-trend-empty">
          <TrendingUp size={32} />
          <span>No spending data for this period</span>
        </div>
      )}
    </div>
  );
}

export default SpendingTrendChart;
